import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { SelectMenu, type MenuOption } from "@majourneys/ui";

export interface CityOption {
  readonly slug: string;
  readonly name: string;
  readonly image?: string | null;
  readonly activitiesCount?: number;
}

interface CityFilterProps {
  readonly cities: readonly CityOption[];
  readonly selectedSlug: string | null;
  readonly onSelect: (slug: string | null) => void;
  readonly allLabel?: string;
  readonly placeholder?: string;
  /** Show the leading "All cities" option (default true). Pass false when a
   *  concrete city must be chosen (e.g. creating a trip). */
  readonly includeAll?: boolean;
  /**
   * A small chip trigger (pin, name, chevron) for a line of text, such as a
   * trip day's heading. `triggerLabel` then names what the menu changes.
   */
  readonly compact?: boolean;
  readonly triggerLabel?: string;
}

/**
 * The Discover city menu.
 *
 * The behaviour is `@majourneys/ui`'s house dropdown now, not a fourth hand
 * copy of open / outside-press / Escape. What it gains: Escape stops at the
 * menu instead of travelling on, Home and End, focus back on the trigger, and
 * the phone body — the twelve-city list comes up as a bottom sheet rather than
 * hanging off a chip. The search field is not asked for here; the shared
 * control turns it on by itself once a list passes about ten rows, which this
 * one always does.
 *
 * The trigger keeps the tuned Discover look via `renderTrigger`: the classes
 * live in `styles/discover.css`, which is the only place this package may put
 * styling, because the app's Tailwind build does not scan it.
 */
export function CityFilter({
  cities,
  selectedSlug,
  onSelect,
  allLabel,
  placeholder,
  includeAll = true,
  compact = false,
  triggerLabel,
}: CityFilterProps) {
  const { t } = useTranslation();
  const allLabelText = allLabel ?? t("discover.all_cities");
  const placeholderText = placeholder ?? t("discover.search_cities");

  const selectedCity = cities.find((c) => c.slug === selectedSlug) ?? null;

  // Busiest first, and "All cities" on top when it is offered. The caller has
  // already dropped every city that leads nowhere; the counts are its work too.
  const options = useMemo<MenuOption[]>(() => {
    const sorted = [...cities].sort(
      (a, b) => (b.activitiesCount ?? 0) - (a.activitiesCount ?? 0),
    );
    const rows: MenuOption[] = sorted.map((city) => ({
      value: city.slug,
      label: city.name,
      meta:
        typeof city.activitiesCount === "number" && city.activitiesCount > 0
          ? t("discover.picks_count", { count: city.activitiesCount })
          : undefined,
      lead: city.image ? (
        <span
          className="mj-city-filter__option-thumb"
          style={{ backgroundImage: `url(${city.image})` }}
          aria-hidden="true"
        />
      ) : (
        <span className="mj-city-filter__option-icon" aria-hidden="true">
          <Icon name="ri-map-pin-2-line" />
        </span>
      ),
    }));
    if (!includeAll) return rows;
    return [
      {
        value: "",
        label: allLabelText,
        lead: (
          <span className="mj-city-filter__option-icon" aria-hidden="true">
            <Icon name="ri-global-line" />
          </span>
        ),
      },
      ...rows,
    ];
  }, [cities, includeAll, allLabelText, t]);

  return (
    <SelectMenu
      name="discover-city"
      className="mj-city-filter"
      options={options}
      value={selectedSlug ?? ""}
      onChange={(next) => onSelect(next === "" ? null : next)}
      width={300}
      side="start"
      labels={{
        title: t("discover.select_city"),
        listLabel: t("discover.cities"),
        searchPlaceholder: placeholderText,
        clearSearch: t("common.menu_clear_search", { defaultValue: "Clear the search" }),
        closeList: t("common.menu_close_list", { defaultValue: "Close the list" }),
        emptyTitle: t("discover.empty_no_picks"),
        emptyHint: t("discover.empty_try_other"),
        noMatchTitle: (query) => t("discover.no_cities_match", { query }),
        noMatchHint: t("common.menu_no_match_hint", {
          defaultValue: "Try a shorter word, or clear the search.",
        }),
      }}
      renderTrigger={({ ref, buttonProps }) =>
        compact ? (
          <button
            {...buttonProps}
            ref={ref}
            className="mj-press mj-city-filter__trigger--compact"
            data-no-drag
            {...(triggerLabel ? { "aria-label": triggerLabel } : {})}
          >
            <Icon name="ri-map-pin-2-line" aria-hidden="true" />
            <span className="mj-city-filter__label">
              {selectedCity ? selectedCity.name : allLabelText}
            </span>
            <Icon name="ri-arrow-down-s-line" aria-hidden="true" />
          </button>
        ) : (
        <button
          {...buttonProps}
          ref={ref}
          className="mj-press mj-city-filter__trigger"
          data-no-drag
        >
          {selectedCity?.image ? (
            <span
              className="mj-city-filter__trigger-thumb"
              style={{ backgroundImage: `url(${selectedCity.image})` }}
              aria-hidden="true"
            />
          ) : (
            /* The pin sits in its own round well, so it is a wrapper rather than
               a bare glyph — an <svg> cannot centre itself inside a 28px disc. */
            <span className="mj-city-filter__pin" aria-hidden="true">
              <Icon name="ri-map-pin-2-line" />
            </span>
          )}
          <span className="mj-city-filter__label">
            {selectedCity ? selectedCity.name : allLabelText}
          </span>
          <Icon name="ri-arrow-down-s-line" className="mj-city-filter__chevron" aria-hidden="true" />
        </button>
        )
      }
    />
  );
}
